import { useTranslation } from "react-i18next";
import { listConnectors } from "../../api/connectorsApi";
import { useLoad } from "../../shared/loading/useLoad";
import Field from "../../ui/forms/Field";
import FieldGroup from "../../ui/forms/FieldGroup";
import { FORM_COMPACT_CONTROL_CLASS } from "../../ui/styles/formStyles";
import { agentLevelsOf, levelChoices, STANDARD_LEVEL, type AgentLevels } from "../profile";

interface InstallationReasoningLevelsProps {
  levels: Readonly<Record<string, string>>;
  onChange?: (levels: Readonly<Record<string, string>>) => void;
}

/**
 * The installation's reasoning level per agent (Johannes, 3 Nov 16:51): the agents and levels
 * the administrator's PCs report, and every agent already set; "Default" is the agent's own.
 */
export default function InstallationReasoningLevels({ levels, onChange }: InstallationReasoningLevelsProps) {
  const { t } = useTranslation();
  const { state } = useLoad(listConnectors);
  const reported = state.status === "loaded" ? agentLevelsOf(state.value) : [];
  const agents: AgentLevels[] = [
    ...reported,
    ...Object.keys(levels).filter((agentId) => !reported.some((agent) => agent.agentId === agentId)).map((agentId) => ({ agentId, title: agentId, efforts: [] })),
  ];
  if (agents.length === 0) return null;
  return (
    <FieldGroup legend={t("administration.installation.reasoningLevels")} explanation={t("administration.installation.reasoningLevelsHint")} className="space-y-2">
      {agents.map((agent) => (
        <Field key={agent.agentId} label={agent.title} layout="inline">
          {(control) => (
            <select {...control} className={FORM_COMPACT_CONTROL_CLASS} disabled={onChange === undefined}
              value={levels[agent.agentId] ?? STANDARD_LEVEL}
              onChange={(event) => {
                const others = Object.fromEntries(Object.entries(levels).filter(([agentId]) => agentId !== agent.agentId));
                onChange?.(event.target.value === STANDARD_LEVEL ? others : { ...others, [agent.agentId]: event.target.value });
              }}>
              {levelChoices(agent, undefined, levels[agent.agentId], t).map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
            </select>
          )}
        </Field>
      ))}
    </FieldGroup>
  );
}
